import React, { ReactNode } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, BrowserRouter } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LoginPage from './components/Login/LoginPage';
import Dashboard from './components/Dashboard/Dashboard';
import Consultants from './ui/pages/Consultants/Consultants';
import { theme } from './theme/theme';
import './styles/globals.css';
import './styles/components.css';

import ConsultationsTable from './ui/pages/Consultations/Consultations';
import AccountDetails from './ui/pages/AccountDetails/AccountDetails';
import ConsultantDetails from './ui/pages/ConsultantDetails/ConsultantDetails';
import CustomerProfile from './ui/pages/CustomerProfile/CustomerProfile';
import BookingDetails from './ui/pages/BookingDetails/BookingDetails';
import UserLocationDetails from './ui/pages/UserLocationDetails/UserLocationDetails';
import KundiChartsPage from './ui/pages/KundiCharts/KundiChartsPage';
import UserSitemapsPage from './ui/pages/UserSitemaps/UserSitemaps';

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="spinner"></div>
      </div>
    );
  }

  return user ? <>{children}</> : <Navigate to="/login" replace />;
};

const protectedRoute = (child: ReactNode) => {
  return (
    <ProtectedRoute>
      {child}
    </ProtectedRoute>
  );
};

const AppContent: React.FC = () => {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/consultations" replace />} />
        <Route path="consultations" element={protectedRoute(<ConsultationsTable />)} />
        <Route path="account/:userId" element={protectedRoute(<AccountDetails />)} />
        <Route path="consultant-details/:staffId" element={protectedRoute(<ConsultantDetails />)} />
        <Route path="user-profiles/:userId/:userProfileId" element={protectedRoute(<CustomerProfile />)} />
        <Route path="consultants" element={protectedRoute(<Consultants />)} />
        <Route path="bookings/:id" element={protectedRoute(<BookingDetails />)} />
        <Route path="user-location/:locationId" element={protectedRoute(<UserLocationDetails />)} />
        <Route path="kundli-charts" element={protectedRoute(<KundiChartsPage />)} />
        <Route path="user-sitemaps" element={protectedRoute(<UserSitemapsPage />)} />
      </Route>
    </Routes>
  );
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;
